import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  Grid, 
  Paper, 
  IconButton, 
  Tooltip,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Avatar,
  AppBar,
  Toolbar,
  TextField,
  Snackbar,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import AddIcon from '@mui/icons-material/Add';
import SalesOutreachShell from './SalesOutreachShell';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PeopleIcon from '@mui/icons-material/People';
import InfoIcon from '@mui/icons-material/Info';
import { useLanguage } from '../context/LanguageContext';
import api from '../api';
import logger from '../utils/logger';

const CompanyResearch = ({ selectedRecords: initialRecords, onClose }) => {
  logger.log('🚀 CompanyResearch component loaded - slimmed down version');
  
  // Simplified state variables
  const [selectedRecords, setSelectedRecords] = useState(initialRecords || []);
  const [outreachDialogOpen, setOutreachDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editingUrl, setEditingUrl] = useState({});
  const [editingLinkedIn, setEditingLinkedIn] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [urlSearchLoading, setUrlSearchLoading] = useState({});
  const { language } = useLanguage();

  // Update selectedRecords when initialRecords changes
  useEffect(() => {
    if (initialRecords && Array.isArray(initialRecords)) {
      setSelectedRecords(initialRecords);
    }
  }, [initialRecords]);

  // Handle company card click - open SalesOutreach
  const handleCompanyClick = (record) => {
    setSelectedCompany(record);
    setOutreachDialogOpen(true);
  };

  // Helper functions to get company data
  const getCompanyDisplayName = (record) => {
    // Use the actual field name from your database
    const name = record.Instellingsnaam || '';
    
    return name || 'Unknown Company';
  };

  const getCompanyAddress = (record) => {
    // Use the actual field names from your database
    const address = record.Vestigingsadres || record.Correspondentieadres || '';
    const street = record['Vestigingsadres Straat'] || record['Correspondentieadres Straat'] || '';
    const houseNumber = record['Vestigingsadres Huisnummer'] || record['Correspondentieadres Huisnummer'] || '';
    const addition = record['Vestigingsadres Toevoeging'] || record['Correspondentieadres Toevoeging'] || '';
    const city = record['Vestigingsadres Plaats'] || record['Correspondentieadres Plaats'] || '';
    const postalCode = record['Vestigingsadres Postcode'] || record['Correspondentieadres Postcode'] || '';
    
    // Build address string
    let addressParts = [];
    
    if (address) {
      addressParts.push(address);
    } else if (street) {
      let streetPart = street;
      if (houseNumber) streetPart += ` ${houseNumber}`;
      if (addition) streetPart += ` ${addition}`;
      addressParts.push(streetPart);
    }
    
    if (postalCode && city) {
      addressParts.push(`${postalCode} ${city}`);
    } else if (city) {
      addressParts.push(city);
    } else if (postalCode) {
      addressParts.push(postalCode);
    }
    
    return addressParts.length > 0 ? addressParts.join(', ') : 
           (language === 'nl' ? 'Geen adres beschikbaar' : 'No address available');
  };

  const getCompanyIndustry = (record) => {
    // Use the actual SBI code description from your database
    const industry = record['SBI-code locatie_Omschrijving'] || 
                    record['SBI-code 2-cijferig Locatie_Omschrijving'] || '';
    
    return industry || (language === 'nl' ? 'Onbekende branche' : 'Unknown industry');
  };

  const getCompanySize = (record) => {
    // Use the actual employee class description from your database
    const size = record['Klasse Aantal Medewerkers Locatie_Omschrijving'] || 
                record['Klasse Aantal Medewerkers Concern_Omschrijving'] || '';
    
    return size || (language === 'nl' ? 'Onbekend' : 'Unknown');
  };

  const getCompanyLocations = (record) => {
    // Use the actual locations class description from your database
    const locations = record['Klasse Aantal Locaties Van Concern_Omschrijving'] || '';
    
    return locations || '1 locatie';
  };

  const getCompanyWebsite = (record) => {
    // Try all possible website field names
    const website = record.Website || record.website || record.URL || record.url ||
                   record.site || record.Site || record.webadres || record.Webadres ||
                   record.homepage || record.Homepage || record.web || record.Web ||
                   record.domain || record.Domain || '';
    return website;
  };

  const getCompanyLinkedIn = (record) => {
    // Try all possible LinkedIn field names
    const linkedin = record['LinkedIn Link'] || record.LinkedIn || record.linkedin || 
                    record.linkedin_url || record.linkedinUrl || record.LinkedInUrl ||
                    record.linkedin_link || record.LinkedinLink || record.linkedIn ||
                    record.LinkedIn_URL || record.linkedin_URL || '';
    return linkedin;
  };

  const getAvatarColor = (name) => {
    const colors = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#0288d1', '#689f38', '#fbc02d'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getAvatarText = (name) => {
    if (!name) return '?';
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // URL update functions
  const updateWebsiteUrl = async (recordId, newUrl) => {
    try {
      const response = await api.updateRecordField(recordId, 'URL', newUrl);
      
      if (response.success) {
        // Update local state
        const updatedRecords = selectedRecords.map(r => {
          if (r.record_id === recordId) {
            return {
              ...r,
              Website: newUrl,
              website: newUrl,
              URL: newUrl,
              url: newUrl
            };
          }
          return r;
        });
        setSelectedRecords(updatedRecords);
        setEditingUrl(prev => ({ ...prev, [recordId]: false }));
        showNotification('Website URL updated successfully', 'success');
    } else {
        showNotification(response.message || 'Failed to update website URL', 'error');
      }
    } catch (err) {
      logger.error('Error updating website URL:', err);
      showNotification('Failed to update website URL', 'error');
    }
  };

  const updateLinkedInUrl = async (recordId, newLinkedInUrl) => {
    try {
      const response = await api.updateRecordField(recordId, 'LinkedIn', newLinkedInUrl);
      
      if (response.success) {
        // Update local state
        const updatedRecords = selectedRecords.map(r => {
          if (r.record_id === recordId) {
            return {
              ...r,
              "LinkedIn Link": newLinkedInUrl,
              LinkedIn: newLinkedInUrl,
              linkedin: newLinkedInUrl,
              linkedin_url: newLinkedInUrl
            };
          }
          return r;
        });
        setSelectedRecords(updatedRecords);
        setEditingLinkedIn(prev => ({ ...prev, [recordId]: false }));
        showNotification('LinkedIn URL updated successfully', 'success');
      } else {
        showNotification(response.message || 'Failed to update LinkedIn URL', 'error');
      }
    } catch (err) {
      logger.error('Error updating LinkedIn URL:', err);
      showNotification('Failed to update LinkedIn URL', 'error');
    }
  };
  
  const searchCompanyUrls = async (recordId) => {
    const record = selectedRecords.find(r => r.record_id === recordId);
    if (!record) return;
    
    setUrlSearchLoading(prev => ({ ...prev, [recordId]: true }));
    
    try {
      const companyName = getCompanyDisplayName(record);
      const response = await api.post('/api/search-company-urls', {
        company_name: companyName,
        record_id: recordId
      });

      if (response.data && response.data.success) {
        const { website_url, linkedin_url } = response.data;
        
        // Update the record with found URLs
        const updatedRecords = selectedRecords.map(r => {
          if (r.record_id === recordId) {
            const updates = { ...r };
            if (website_url && !getCompanyWebsite(r)) {
              updates.Website = website_url;
              updates.website = website_url;
              updates.URL = website_url;
              updates.url = website_url;
            }
            if (linkedin_url && !getCompanyLinkedIn(r)) {
              updates["LinkedIn Link"] = linkedin_url;
              updates.LinkedIn = linkedin_url;
              updates.linkedin = linkedin_url;
              updates.linkedin_url = linkedin_url;
            }
            return updates;
          }
          return r;
        });
        
        setSelectedRecords(updatedRecords);
    showNotification(
          `Found URLs for ${companyName}${website_url ? ' (Website)' : ''}${linkedin_url ? ' (LinkedIn)' : ''}`,
          'success'
        );
      } else {
        showNotification('No URLs found for this company', 'info');
      }
    } catch (error) {
      logger.error('Error searching company URLs:', error);
      showNotification('Failed to search company URLs', 'error');
    } finally {
      setUrlSearchLoading(prev => ({ ...prev, [recordId]: false }));
    }
  };

  const showNotification = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <AppBar position="static" color="primary" sx={{ mb: 2 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {language === 'nl' ? "Bedrijfsonderzoek" : "Company Research"}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Grid container spacing={1}>
        {selectedRecords.map((record) => (
          <Grid item xs={12} key={record.record_id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: 2
                }
              }}
              onClick={() => handleCompanyClick(record)}
            >
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    sx={{
                      mr: 1.5,
                      bgcolor: getAvatarColor(getCompanyDisplayName(record)),
                      width: 36,
                      height: 36,
                      fontSize: '0.9rem'
                    }}
                  >
                    {getAvatarText(getCompanyDisplayName(record))}
                  </Avatar>
                  
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    {/* Company Name */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.25, lineHeight: 1.2 }}>
                      {getCompanyDisplayName(record)}
                    </Typography>
                    
                    {/* Company Details in one line */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                      {/* Address */}
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <LocationOnIcon fontSize="small" sx={{ mr: 0.3, color: 'text.secondary', flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          maxWidth: '200px'
                        }}>
                          {getCompanyAddress(record)}
                        </Typography>
                      </Box>
                      
                      {/* Industry */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <InfoIcon fontSize="small" sx={{ mr: 0.3, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {getCompanyIndustry(record)}
                        </Typography>
                      </Box>
                      
                      {/* Company Size */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PeopleIcon fontSize="small" sx={{ mr: 0.3, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {getCompanySize(record)}
                        </Typography>
                      </Box>
                      
                      {/* Locations */}
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon fontSize="small" sx={{ mr: 0.3, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {getCompanyLocations(record)} {language === 'nl' ? 'loc.' : 'loc.'}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* URLs Row - more compact */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      {/* Website URL */}
                      {editingUrl[record.record_id] ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TextField 
                            value={editingUrl[record.record_id]?.value || ''}
                            onChange={(e) => setEditingUrl(prev => ({
                              ...prev,
                              [record.record_id]: { ...prev[record.record_id], value: e.target.value }
                            }))}
                            placeholder="Website URL"
                            size="small"
                            sx={{ mr: 0.5, minWidth: 150, fontSize: '0.75rem' }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateWebsiteUrl(record.record_id, editingUrl[record.record_id]?.value || '');
                            }}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingUrl(prev => ({ ...prev, [record.record_id]: false }));
                            }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LanguageIcon fontSize="small" sx={{ mr: 0.3, color: 'text.secondary' }} />
                          {getCompanyWebsite(record) ? (
                            <>
                              <Typography 
                                variant="caption" 
                                component="a"
                                href={getCompanyWebsite(record)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  mr: 0.3,
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {getCompanyWebsite(record).replace(/^https?:\/\//, '').replace(/\/$/, '').substring(0, 25)}
                                {getCompanyWebsite(record).replace(/^https?:\/\//, '').replace(/\/$/, '').length > 25 ? '...' : ''}
                              </Typography>
                              <IconButton 
                                size="small"
                                sx={{ 
                                  p: 0.25,
                                  opacity: 0.3,
                                  '&:hover': { opacity: 0.7 }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingUrl(prev => ({ 
                                    ...prev, 
                                    [record.record_id]: { value: getCompanyWebsite(record) }
                                  }));
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.3 }}>
                                {language === 'nl' ? 'Geen website' : 'No website'}
                              </Typography>
                              <IconButton 
                                size="small"
                                sx={{ 
                                  p: 0.25,
                                  opacity: 0.3,
                                  '&:hover': { opacity: 0.7 }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingUrl(prev => ({ 
                                    ...prev, 
                                    [record.record_id]: { value: '' }
                                  }));
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      )}
                      
                      {/* LinkedIn URL */}
                      {editingLinkedIn[record.record_id] ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                          <TextField 
                            value={editingLinkedIn[record.record_id]?.value || ''}
                            onChange={(e) => setEditingLinkedIn(prev => ({
                              ...prev,
                              [record.record_id]: { ...prev[record.record_id], value: e.target.value }
                            }))}
                            placeholder="LinkedIn URL"
                            size="small"
                            sx={{ mr: 0.5, minWidth: 150 }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateLinkedInUrl(record.record_id, editingLinkedIn[record.record_id]?.value || '');
                            }}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingLinkedIn(prev => ({ ...prev, [record.record_id]: false }));
                            }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                          <LinkedInIcon fontSize="small" sx={{ mr: 0.3, color: '#0077b5' }} />
                          {getCompanyLinkedIn(record) ? (
                            <>
                              <Typography 
                                variant="caption" 
                                component="a"
                                href={getCompanyLinkedIn(record)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: '#0077b5',
                                  textDecoration: 'none',
                                  mr: 0.3,
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                LinkedIn
                              </Typography>
                              <IconButton 
                                size="small"
                                sx={{ 
                                  p: 0.25,
                                  opacity: 0.3,
                                  '&:hover': { opacity: 0.7 }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingLinkedIn(prev => ({ 
                                    ...prev, 
                                    [record.record_id]: { value: getCompanyLinkedIn(record) }
                                  }));
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.3 }}>
                                {language === 'nl' ? 'Geen LinkedIn' : 'No LinkedIn'}
                              </Typography>
                              <IconButton 
                                size="small"
                                sx={{ 
                                  p: 0.25,
                                  opacity: 0.3,
                                  '&:hover': { opacity: 0.7 }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingLinkedIn(prev => ({ 
                                    ...prev, 
                                    [record.record_id]: { value: '' }
                                  }));
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      )}
                      
                      {/* Auto-search URLs button */}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SearchIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          searchCompanyUrls(record.record_id);
                        }}
                        disabled={urlSearchLoading[record.record_id]}
                        sx={{ ml: 'auto', py: 0.25, px: 1, fontSize: '0.7rem', minWidth: 'auto' }}
                      >
                        {urlSearchLoading[record.record_id] ? 
                          (language === 'nl' ? 'Zoeken...' : 'Searching...') :
                          (language === 'nl' ? 'Zoek URLs' : 'Search URLs')
                        }
                      </Button>
                    </Box>
                        </Box>
              </Box>
              </CardContent>
            </Card>
                    </Grid>
        ))}
                    </Grid>
      
      {/* Sales Outreach */}
      {selectedCompany && (
        <SalesOutreachShell
          open={outreachDialogOpen}
          onClose={() => setOutreachDialogOpen(false)}
          recordId={selectedCompany.record_id}
          companyName={getCompanyDisplayName(selectedCompany)}
          contactInfo={selectedCompany}
          openWithUrlSearch={false}
        />
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
                </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyResearch; 
